// #2121 v2 - Vista del flujo de derivación: bandeja tipo mail con quick-stats, multi-filtros,
// bulk-bar y banner de "+N nuevos" cuando llegan eventos en vivo y no se pueden prepend.
import React, { useEffect, useState } from 'react';
import {
  Card, CardBody,
  Button, Alert,
} from 'reactstrap';
import { useTranslate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import DeviceStudiesTable      from 'components/DeviceStudies/DeviceStudiesTable';
import AssignDoctorModal       from 'components/DeviceStudies/AssignDoctorModal';
import DerivationHistoryModal  from 'components/DeviceStudies/DerivationHistoryModal';
import {
  actionGetDeviceStudies,
  actionSetDeviceView,
  actionSetDeviceSearch,
  actionSetDevicePageIndex,
  actionGetDeviceTypes,
  actionSetDeviceTypeFilter,
  actionApplyPendingNew,
  actionOpenAssignModal,
  actionGetAssignableDoctors,
  actionClearSelection,
} from 'store/actions/deviceStudiesActions';

const DeviceStudiesList = () => {
  const dispatch = useDispatch();
  const t        = useTranslate('device-studies');
  const { register, handleSubmit, reset } = useForm();
  const [showTypeMenu, setShowTypeMenu] = useState(false);

  const practice = useSelector((s) => s.practicesReducer.practice);
  const {
    studies, page, lastPage, totalEntries, limit,
    view, search, dateFrom, dateTo, sortDate, loadingList,
    deviceTypes, deviceTypeFilter, pendingNewCount, selectedIds, stats,
  } = useSelector((s) => s.deviceStudiesReducer);

  // Carga inicial + cuando cambia practice
  useEffect(() => {
    if (!practice?._id) return;
    dispatch(actionGetDeviceStudies({
      practiceId: practice._id, page, limit, datesort: sortDate, view, search,
      from: dateFrom, to: dateTo, deviceType: deviceTypeFilter,
    }));
    dispatch(actionGetDeviceTypes());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice?._id]);

  // Re-fetch cuando cambia la página (los demás filtros disparan refetch en saga)
  useEffect(() => {
    if (!practice?._id) return;
    dispatch(actionGetDeviceStudies({}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Stats vienen del backend (endpoint dedicado, independiente del view filter)

  const onTabChange = (tab) => {
    if (tab !== view) dispatch(actionSetDeviceView(tab));
  };

  const onSearch = ({ query }) => {
    dispatch(actionSetDeviceSearch(query || ''));
  };

  const onClear = () => {
    reset({ query: '' });
    dispatch(actionSetDeviceSearch(''));
  };

  const toggleType = (type) => {
    const next = deviceTypeFilter.includes(type)
      ? deviceTypeFilter.filter((x) => x !== type)
      : [...deviceTypeFilter, type];
    dispatch(actionSetDeviceTypeFilter(next));
  };

  const openBulkAssign = () => {
    if (selectedIds.length === 0) return;
    dispatch(actionOpenAssignModal(selectedIds));
    dispatch(actionGetAssignableDoctors(selectedIds[0], practice?._id));
  };

  const emptyText = view === 'inbox' ? t('empty-inbox')
                  : view === 'assigned' ? t('empty-assigned')
                  : t('empty-all');

  return (
    <>
      <Card style={{ marginBottom: 60 }}>
        <CardBody>

          {/* Quick stats — strip compacta horizontal */}
          <div
            className="d-flex flex-wrap align-items-center mb-3"
            style={{
              gap: '20px',
              padding: '8px 12px',
              background: '#f7f8fa',
              borderRadius: 8,
              fontSize: '0.875rem',
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5e72e4', display: 'inline-block' }} />
              <span className="text-muted">{t('stat-pending')}:</span>
              <strong>{stats.pending}</strong>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2dce89', display: 'inline-block' }} />
              <span className="text-muted">{t('stat-derived-today')}:</span>
              <strong>{stats.derivedToday}</strong>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f5365c', display: 'inline-block' }} />
              <span className="text-muted">{t('stat-stale')}:</span>
              <strong style={{ color: stats.stale > 0 ? '#f5365c' : 'inherit' }}>{stats.stale}</strong>
            </div>
          </div>

          {/* Banner +N nuevos (cuando llegaron eventos pero no se pudo prepend) */}
          {pendingNewCount > 0 && (
            <Alert
              color="info"
              className="d-flex justify-content-between align-items-center"
            >
              <span>{t('pending-new').replace('{n}', pendingNewCount)}</span>
              <Button color="info" size="sm" onClick={() => dispatch(actionApplyPendingNew())}>
                {t('btn-load-new')}
              </Button>
            </Alert>
          )}

          {/* Filtros — search a la izquierda (expandible), tipos a la derecha */}
          <div
            className="d-flex flex-wrap align-items-center mb-3"
            style={{ gap: '12px' }}
          >
            <form
              onSubmit={handleSubmit(onSearch)}
              className="d-flex align-items-center flex-grow-1"
              style={{ gap: '6px', maxWidth: 520 }}
            >
              <input
                ref={register}
                name="query"
                placeholder={t('search-placeholder')}
                className="form-control form-control-sm flex-grow-1"
              />
              <button type="submit" className="btn btn-info btn-sm" style={{ whiteSpace: 'nowrap' }}>
                {t('btn-search')}
              </button>
              {search && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={onClear}
                  title={t('btn-clear-filter')}
                >
                  ✕
                </button>
              )}
            </form>

            {/* Multi-select de tipos — alineado a la derecha */}
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={() => setShowTypeMenu((x) => !x)}
                style={{
                  background:    deviceTypeFilter.length > 0 ? '#5e72e4' : '#fff',
                  color:         deviceTypeFilter.length > 0 ? '#fff'    : '#525f7f',
                  border:        '1px solid #5e72e4',
                  borderRadius:  20,
                  padding:       '4px 14px',
                  fontSize:      '0.875rem',
                  cursor:        'pointer',
                  whiteSpace:    'nowrap',
                }}
              >
                {deviceTypeFilter.length > 0
                  ? `${t('filter-types')} (${deviceTypeFilter.length})`
                  : t('filter-types-all')}
                {' ▾'}
              </button>
              {showTypeMenu && (
                <>
                  <div
                    onClick={() => setShowTypeMenu(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                  />
                  <div
                    className="card shadow"
                    style={{
                      position: 'absolute', zIndex: 100, top: 'calc(100% + 4px)', right: 0,
                      minWidth: 220, padding: 10,
                      background: '#fff',
                    }}
                  >
                    {deviceTypes.length === 0 ? (
                      <span className="text-muted small">{t('no-types')}</span>
                    ) : (
                      deviceTypes.map((tp) => (
                        <label key={tp} className="d-flex align-items-center mb-1" style={{ gap: 8, cursor: 'pointer', margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={deviceTypeFilter.includes(tp)}
                            onChange={() => toggleType(tp)}
                          />
                          <span>{tp}</span>
                        </label>
                      ))
                    )}
                    {deviceTypeFilter.length > 0 && (
                      <Button
                        size="sm"
                        color="link"
                        className="p-0 mt-2"
                        onClick={() => dispatch(actionSetDeviceTypeFilter([]))}
                      >
                        {t('btn-clear-filter')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tabs (debajo del filtro) — estilo tab plano con underline */}
          <div
            role="tablist"
            style={{
              display:      'flex',
              borderBottom: '2px solid #e9ecef',
              gap:          0,
            }}
          >
            {[
              { k: 'inbox',    label: t('tab-inbox'),    count: stats.pending },
              { k: 'assigned', label: t('tab-assigned'), count: null },
              { k: 'all',      label: t('tab-all'),      count: null },
            ].map((tab) => {
              const active = view === tab.k;
              return (
                <div
                  key={tab.k}
                  role="tab"
                  aria-selected={active}
                  onClick={() => onTabChange(tab.k)}
                  style={{
                    padding:      '10px 24px',
                    cursor:       'pointer',
                    fontSize:     '0.95rem',
                    fontWeight:   active ? 600 : 400,
                    color:        active ? '#5e72e4' : '#525f7f',
                    borderBottom: active ? '2px solid #5e72e4' : '2px solid transparent',
                    marginBottom: -2,
                    background:   'transparent',
                    border:       'none',
                    borderBottomColor: active ? '#5e72e4' : 'transparent',
                    borderBottomWidth: 2,
                    borderBottomStyle: 'solid',
                    transition:   'color 0.15s, border-color 0.15s',
                  }}
                >
                  {tab.label}
                  {tab.count != null && tab.count > 0 && (
                    <span
                      className="ml-2"
                      style={{
                        fontSize: '0.75rem',
                        color:    active ? '#5e72e4' : '#999',
                      }}
                    >
                      {tab.count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bulk bar */}
          {selectedIds.length > 0 && (
            <Alert color="primary" className="d-flex justify-content-between align-items-center mt-3">
              <span>{t('bulk-selected').replace('{n}', selectedIds.length)}</span>
              <div className="d-flex" style={{ gap: 8 }}>
                <Button color="primary" size="sm" onClick={openBulkAssign}>
                  {t('btn-bulk-assign')}
                </Button>
                <Button color="link" size="sm" onClick={() => dispatch(actionClearSelection())}>
                  {t('btn-clear-selection')}
                </Button>
              </div>
            </Alert>
          )}

          {/* Tabla o vacío */}
          <div className={selectedIds.length > 0 ? '' : 'mt-3'}>
            {!loadingList && studies.length === 0 ? (
              <p className="text-center text-muted py-4">{emptyText}</p>
            ) : (
              <DeviceStudiesTable studies={studies} loadingList={loadingList} />
            )}
          </div>

          {/* Paginación */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => dispatch(actionSetDevicePageIndex(page - 1))}
              style={{
                background:    page === 0 ? '#f7f8fa' : '#5e72e4',
                color:         page === 0 ? '#999'    : '#fff',
                border:        '1px solid #5e72e4',
                borderRadius:  20,
                padding:       '4px 16px',
                fontSize:      '0.875rem',
                cursor:        page === 0 ? 'not-allowed' : 'pointer',
                opacity:       page === 0 ? 0.6 : 1,
              }}
            >
              ← {t('pagination-prev')}
            </button>
            <span className="text-muted small">
              {t('pagination').replace('{page}', page + 1).replace('{last}', lastPage + 1).replace('{total}', totalEntries)}
            </span>
            <button
              type="button"
              disabled={page >= lastPage}
              onClick={() => dispatch(actionSetDevicePageIndex(page + 1))}
              style={{
                background:    page >= lastPage ? '#f7f8fa' : '#5e72e4',
                color:         page >= lastPage ? '#999'    : '#fff',
                border:        '1px solid #5e72e4',
                borderRadius:  20,
                padding:       '4px 16px',
                fontSize:      '0.875rem',
                cursor:        page >= lastPage ? 'not-allowed' : 'pointer',
                opacity:       page >= lastPage ? 0.6 : 1,
              }}
            >
              {t('pagination-next')} →
            </button>
          </div>
        </CardBody>
      </Card>

      <AssignDoctorModal />
      <DerivationHistoryModal />
    </>
  );
};

export default DeviceStudiesList;
// #2121 - End
